import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Button,
  Card,
  Input,
  Stack,
  Text,
} from '@ltvco/refresh-lib/theme';
import { useSession } from '@ltvco/refresh-lib/v1';
import {
  useContext,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';

const BUMPER_DOMAIN = 'https://www.bumper.com';
const BUILDING_REPORT_PATH = 'lp/8dc2dc/3/building-report';
const LANDING_BASE_URL = `${BUMPER_DOMAIN}/?utm_source=bumper&utm_medium=display`;

export interface IVinSearchAdProps {
  /** Placement identifier used in the GA label and UTM campaign (e.g. 'ListingsPageInContent', 'ListingsPageBottom'). */
  location: string;
  /** Renders as a wide bottom-of-page banner instead of an in-grid card. */
  bannerAd?: boolean;
}

/**
 * In-feed / bottom-of-page ad promoting Bumper's own VIN search, ported from
 * the Ember `ads/vin-search-ad` component — **Bumper-only**. Ember's version
 * A/B tests this same ad between promoting Bumper and a sister brand
 * (BeenVerified) via a Google Optimize flag; that cross-brand variant is
 * deliberately out of scope here (split into its own ticket — no equivalent
 * GrowthBook flag was found, and which brand to promote is a business call,
 * not a technical one). This always promotes Bumper.
 *
 * Clicking the ad body (outside the form) opens Bumper's marketing landing
 * page; submitting a VIN opens the same VIN-report flow as the VDP page's
 * "Check Report" action (authenticated → the report directly, otherwise the
 * building-report page), both UTM-tagged for this placement.
 */
export function VinSearchAd({ location, bannerAd = false }: IVinSearchAdProps) {
  const [vin, setVin] = useState('');
  const { trackEvent } = useContext(AppConfig);
  const {
    session: { isAuthenticated },
  } = useSession();

  const utmContent = bannerAd ? '750x80' : '300x550';
  const landingUrl = `${LANDING_BASE_URL}&utm_campaign=BR_VIN_DIS_XBR_CAR_${location}&utm_content=${utmContent}`;

  const fireAdEvent = (): void => {
    trackEvent('Marketplace', 'Ad', `BUMPER-${location}`);
  };

  const openUrl = (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleAdClick = (): void => {
    fireAdEvent();
    openUrl(landingUrl);
  };

  const handleAdKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAdClick();
    }
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    fireAdEvent();

    if (!vin) {
      openUrl(landingUrl);
      return;
    }
    const vinUpper = vin.toUpperCase();
    const utmSuffix = `utm_source=bumper&utm_medium=display&utm_campaign=BR_VIN_DIS_XBR_CAR_${location}`;
    const reportUrl = isAuthenticated
      ? `${BUMPER_DOMAIN}/rf/report/vehicle?vin=${vinUpper}&${utmSuffix}`
      : `${BUMPER_DOMAIN}/${BUILDING_REPORT_PATH}?vin=${vinUpper}&${utmSuffix}`;
    openUrl(reportUrl);
  };

  return (
    <Card
      onClick={handleAdClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleAdKeyDown}
      aria-label="Research a vehicle by VIN number on Bumper"
      sx={{ height: '100%', p: 2, cursor: 'pointer' }}
    >
      <Stack
        spacing={2}
        direction={bannerAd ? { xs: 'column', sm: 'row' } : 'column'}
        alignItems={bannerAd ? 'center' : 'stretch'}
      >
        <Text variant="subtitle1" fontWeight={600}>
          Research a Vehicle By VIN Number
        </Text>
        <Text variant="body2" color="text.secondary">
          Learn more about a vehicle you own or plan to buy.
        </Text>
        <Box sx={{ width: '100%' }}>
          <form
            onSubmit={handleSearchSubmit}
            onClick={(event) => event.stopPropagation()}
          >
            <Stack direction="row" spacing={1}>
              <Input
                placeholder="Enter a VIN"
                value={vin}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setVin(event.target.value.replace(/\W/g, ''))
                }
                fullWidth
                inputProps={{ 'data-testid': 'vin-search-ad-input' }}
              />
              <Button
                type="submit"
                variant="contained"
                data-testid="vin-search-ad-submit"
              >
                Search
              </Button>
            </Stack>
          </form>
        </Box>
      </Stack>
    </Card>
  );
}
